import { Box, CircularProgress, Modal } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import moment from "moment";
import { useState } from "react";
import {
  cancelTransactionPbf,
  completeTransactionPbf,
} from "../../../../services/purchaseApi";

const ACTIONS = {
  cancel: {
    question: "Batalkan pesanan ini?",
    note: "Pesanan yang dibatalkan tidak dapat dikembalikan.",
    confirm: "Ya, Batalkan",
    mutationFn: cancelTransactionPbf,
  },
  complete: {
    question: "Selesaikan pesanan ini?",
    note: "Pastikan semua barang sudah diterima dalam kondisi baik.",
    confirm: "Ya, Selesai",
    mutationFn: completeTransactionPbf,
  },
};

/**
 * Cancel / complete buttons for one PBF invoice, shown only when be-olin-pbf
 * says the action is allowed (can_cancel / can_complete), plus the
 * auto-complete time of an arrived order. Used by the list and the detail.
 */
const TransactionActions = ({ invoice, onChanged, onError }) => {
  const [pending, setPending] = useState(null);

  const { mutate, isLoading } = useMutation({
    mutationFn: ({ action }) =>
      ACTIONS[action].mutationFn({ invoice_uid: invoice.invoice_uid }),
    onSuccess: () => {
      setPending(null);
      onChanged?.();
    },
    onError: (err) => {
      setPending(null);
      onError?.(err?.message?.id ? err.message.id : "Gagal memproses pesanan");
    },
  });

  if (!invoice?.can_cancel && !invoice?.can_complete) return null;
  const action = pending ? ACTIONS[pending] : null;

  return (
    <div className="flex gap-2 items-center">
      {invoice.auto_complete_at ? (
        <div className="w-full">
          <p>Selesai otomatis pada:</p>
          <p className="text-red-500 font-semibold">
            {moment(invoice.auto_complete_at).format("DD MMM YYYY HH:mm")}
          </p>
        </div>
      ) : null}
      {invoice.can_cancel ? (
        <button
          type="button"
          className="text-red-500 bg-white border border-red-500 whitespace-nowrap hover:bg-gray-100 rounded-md w-fit py-2.5 px-4 text-[14px] disabled:bg-gray-300"
          disabled={isLoading}
          onClick={() => setPending("cancel")}
        >
          Batalkan
        </button>
      ) : null}
      {invoice.can_complete ? (
        <button
          type="button"
          className="text-white bg-[#94C679] whitespace-nowrap hover:bg-gray-300 rounded-md w-fit py-2.5 px-4 text-[14px] disabled:bg-gray-300"
          disabled={isLoading}
          onClick={() => setPending("complete")}
        >
          Pesanan Selesai
        </button>
      ) : null}

      <Modal open={!!action} onClose={() => !isLoading && setPending(null)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            maxWidth: "90vw",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: 3,
          }}
          className="text-left text-[14px] text-[#2C3E5D]"
        >
          <p className="font-bold text-lg mb-2">{action?.question}</p>
          <p className="mb-6">{action?.note}</p>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              className="border border-gray-400 rounded-md py-2 px-4 hover:bg-gray-100"
              disabled={isLoading}
              onClick={() => setPending(null)}
            >
              Batal
            </button>
            <button
              type="button"
              className="text-white bg-[#94C679] rounded-md py-2 px-4 hover:bg-gray-300 disabled:bg-gray-300"
              disabled={isLoading}
              onClick={() => mutate({ action: pending })}
            >
              {isLoading ? <CircularProgress size={16} /> : action?.confirm}
            </button>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default TransactionActions;
